import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
} from 'd3-force';
import type { BubbleEdge, BubbleNode, MapData } from '@pleco/shared';
import { fitsInside, formatPercent, formatUsd, layoutLabels, pctLabel, shortAddress, type LabelRequest } from '@pleco/shared';
import { BRAND } from '../brand.js';

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

/** HTML controls drawn over the map; labels are laid out around them. */
const LABEL_OBSTACLES =
  '.tv-key, .tv-key-btn, .tv-zoom, .la, .tv-verdict-pill, .tv-status, .tv-filter, .legend, .map-toolbar > *, .zoom-controls, .lp-caption, .lp-readout';

interface SimNode extends BubbleNode {
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  fx?: number | null;
  fy?: number | null;
  /** Drawn radius; eases toward rT so live size changes glide. */
  r: number;
  rT: number;
  /** Opacity 0..1; eases toward visT so bubbles fade in / out. */
  vis: number;
  visT: number;
  /** Added from live trades (not in the map snapshot). */
  liveAdded?: boolean;
}

interface SimLink extends Omit<BubbleEdge, 'source' | 'target'> {
  source: SimNode;
  target: SimNode;
}

interface Transform {
  k: number;
  x: number;
  y: number;
}

/**
 * Trader-group layer from the trading terminal, keyed by wallet address.
 * When present it replaces cluster colouring: bubbles take their group's
 * colour, group members are pulled together, and badges mark roles.
 */
export interface MapOverlay {
  colors: Map<string, string>;
  groupOf: Map<string, string>;
  badges: Map<string, { text: string; color: string }>;
  /** Linked groups drawn as a ring with a risk tag ("BUNDLE · 22% · SELLING"). */
  groups?: { id: string; members: string[]; color: string; level: 'high' | 'medium' | 'low'; tag: string; percent: number }[];
  /** Extra tooltip line per wallet (group name, tags). */
  describe?: (address: string) => ReactNode;
}

/** A trade that just happened: the bubble flashes green / red. */
export interface MapPulse {
  address: string;
  side: 'buy' | 'sell';
  at: number;
}

export interface CanvasApi {
  exportPng: (filename: string, title?: string) => void;
  reheat: () => void;
  fit: () => void;
  zoomBy: (factor: number) => void;
}

export interface BubbleCanvasProps {
  data: MapData;
  /** Colour only by the overlay's groups (grey until it arrives), never by
   *  the engine's transfer clusters — one notion of "linked" per screen. */
  groupsOnly?: boolean;
  overlay?: MapOverlay;
  pulses?: MapPulse[];
  /** Live holdings (address → % of supply) replayed from trades since the
   *  snapshot. Bubbles resize smoothly, wallets that sold out fade away and
   *  new large buyers fade in. */
  live?: Map<string, number>;
  selectedId?: string;
  highlightIds?: Set<string>;
  focusMode: boolean;
  showLabels: boolean;
  /** Render exchange / router / contract nodes. Off by default — they are
   *  connected to everyone and turn the map into a hairball. */
  showInfrastructure?: boolean;
  /** Disable all pointer interaction (used by the landing-page preview). */
  staticView?: boolean;
  onSelect: (node?: BubbleNode) => void;
  /** Called from the empty-state button when every holder is hidden infrastructure. */
  onShowInfrastructure?: () => void;
  onHover?: (node?: BubbleNode) => void;
  /** Receives an exporter so toolbars can trigger a PNG download. */
  onReady?: (api: CanvasApi) => void;
}

/* ------------------------------------------------------------------ *
 * Visual helpers
 * ------------------------------------------------------------------ */

/**
 * Independent holders get soft, desaturated tints (teal, sage, slate,
 * dusty lavender) so a normal map isn't a wall of grey, while staying far
 * from the saturated reds / ambers / blues that mark linked groups. The
 * tint is picked from the address, so a wallet keeps its colour.
 */
const SOLO = '#5d636f';
const SOLO_SOFT = ['#4f8f8a', '#5d8aa0', '#6f957c', '#857fa8', '#6b8296', '#7a9a92', '#8c8aa6', '#5f9aa6'];
const softTint = (address: string): string => {
  let h = 0;
  for (let k = 0; k < address.length; k++) h = (h * 33 + address.charCodeAt(k)) >>> 0;
  return SOLO_SOFT[h % SOLO_SOFT.length]!;
};
const KIND_COLOR: Record<string, string> = {
  cex: '#8b93a3',
  dex: '#767e8e',
  contract: '#6a7180',
  bridge: '#767e8e',
  burn: '#4a4f59',
  lp: '#767e8e',
  treasury: '#767e8e',
  eoa: SOLO,
  unknown: '#50555f',
};

const baseColor = (n: SimNode): string => (n.kind === 'eoa' || n.kind === 'unknown' ? softTint(n.address) : (KIND_COLOR[n.kind] ?? SOLO));
const nodeColor = (n: SimNode, clusterColors: Map<number, string>, overlay?: MapOverlay, groupsOnly?: boolean): string => {
  if (overlay) return overlay.colors.get(n.address) ?? baseColor(n);
  if (!groupsOnly && n.clusterId && clusterColors.has(n.clusterId)) return clusterColors.get(n.clusterId)!;
  return baseColor(n);
};

const radiusFor = (percent: number): number => Math.min(74, 4.5 + Math.sqrt(Math.max(0, percent)) * 12.5);

const EDGE_COLOR: Record<string, string> = {
  transfer: 'rgba(205, 210, 220, 0.38)',
  magic: 'rgba(205, 210, 220, 0.38)',
  time: 'rgba(205, 210, 220, 0.32)',
  funding: 'rgba(205, 210, 220, 0.38)',
};

/** Deterministic seed so the same token always lays out the same way. */
function seed(id: string, i: number, total: number): { x: number; y: number } {
  let h = 0;
  for (let k = 0; k < id.length; k++) h = (h * 31 + id.charCodeAt(k)) >>> 0;
  const golden = Math.PI * (3 - Math.sqrt(5));
  const t = (i + 0.5) / Math.max(1, total);
  const r = 340 * Math.sqrt(t) + (h % 17);
  const a = i * golden + (h % 100) / 400;
  return { x: Math.cos(a) * r, y: Math.sin(a) * r };
}

/**
 * A spot for a bubble that joins a laid-out map: the first position on a
 * spiral (starting at an angle derived from the address) that overlaps
 * nobody, a little outside the crowded middle.
 */
function freeSpot(address: string, r: number, nodes: { x: number; y: number; r: number }[]): { x: number; y: number } {
  let h = 0;
  for (let k = 0; k < address.length; k++) h = (h * 31 + address.charCodeAt(k)) >>> 0;
  let cx = 0;
  let cy = 0;
  let ext = 0;
  if (nodes.length) {
    for (const n of nodes) (cx += n.x), (cy += n.y);
    cx /= nodes.length;
    cy /= nodes.length;
    for (const n of nodes) ext = Math.max(ext, Math.hypot(n.x - cx, n.y - cy));
  }
  const a0 = ((h % 3600) / 3600) * Math.PI * 2;
  for (let i = 0; i < 400; i++) {
    const d = ext * 0.35 + i * 3.2;
    const a = a0 + i * 0.47;
    const x = cx + Math.cos(a) * d;
    const y = cy + Math.sin(a) * d;
    if (nodes.every((n) => Math.hypot(n.x - x, n.y - y) > n.r + r + 6)) return { x, y };
  }
  return { x: cx + Math.cos(a0) * (ext + r + 20), y: cy + Math.sin(a0) * (ext + r + 20) };
}

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

export function BubbleCanvas({
  data,
  groupsOnly = false,
  overlay,
  pulses,
  live,
  selectedId,
  highlightIds,
  focusMode,
  showLabels,
  showInfrastructure = false,
  staticView = false,
  onSelect,
  onShowInfrastructure,
  onHover,
  onReady,
}: BubbleCanvasProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const simRef = useRef<Simulation<SimNode, undefined> | null>(null);
  const nodesRef = useRef<SimNode[]>([]);
  const linksRef = useRef<SimLink[]>([]);
  const transformRef = useRef<Transform>({ k: 1, x: 0, y: 0 });
  const sizeRef = useRef({ w: 0, h: 0 });
  const dragRef = useRef<{ node?: SimNode; panning: boolean; lastX: number; lastY: number; moved: boolean }>({
    panning: false,
    lastX: 0,
    lastY: 0,
    moved: false,
  });

  const [hoverNode, setHoverNode] = useState<SimNode | undefined>();
  const overlayRef = useRef<MapOverlay | undefined>(overlay);
  overlayRef.current = overlay;
  const pulsesRef = useRef<MapPulse[]>([]);
  pulsesRef.current = pulses ?? [];
  const liveRef = useRef<Map<string, number> | undefined>(live);
  liveRef.current = live;
  /** Bubbles on their way out (sold out / dropped from the map), drawn fading. */
  const ghostsRef = useRef<SimNode[]>([]);
  const lastFrameRef = useRef(0);
  const relaxRef = useRef<{ timer?: ReturnType<typeof setTimeout>; last: number }>({ last: 0 });
  const reduceMotion = typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  /** Redraw only when something changed: idle maps cost no CPU/battery. */
  const dirtyRef = useRef(true);
  /** Layout glide: settled positions are computed off-screen, then bubbles ease there once. */
  const tweenRef = useRef<{ nodes: SimNode[]; from: { x: number; y: number }[]; to: { x: number; y: number }[]; start: number; dur: number } | null>(null);
  const camTweenRef = useRef<{ from: { k: number; x: number; y: number }; to: { k: number; x: number; y: number }; start: number; dur: number } | null>(null);
  const fittedKeyRef = useRef('');
  const touchesRef = useRef(new Map<number, { x: number; y: number }>());
  /** Pinch start: finger distance, zoom, and the world point under the fingers. */
  const pinchRef = useRef<{ d: number; k: number; wx: number; wy: number } | undefined>(undefined);

  const clusterColors = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of data.clusters) m.set(c.id, c.color);
    return m;
  }, [data.clusters]);

  /** Run the force layout to rest off-screen, then glide every bubble there. */
  function settle(alpha: number) {
    const sim = simRef.current;
    const nodes = nodesRef.current;
    if (!sim || !nodes.length) return;
    // live updates add and remove bubbles: keep the simulation's lists in step
    if (sim.nodes() !== nodes) {
      sim.nodes(nodes);
      (sim.force('link') as ReturnType<typeof forceLink<SimNode, SimLink>> | undefined)?.links(linksRef.current);
    }
    // finish any glide in progress first, so we start from where bubbles are drawn
    const cur = tweenRef.current;
    if (cur) cur.nodes.forEach((n, i) => ((n.x = cur.to[i]!.x), (n.y = cur.to[i]!.y)));
    const from = nodes.map((n) => ({ x: n.x, y: n.y }));
    sim.alpha(alpha);
    const steps = Math.min(420, Math.ceil(Math.log(sim.alphaMin() / alpha) / Math.log(1 - sim.alphaDecay())));
    sim.tick(Math.max(1, steps));
    sim.stop();
    const to = nodes.map((n) => ({ x: n.x, y: n.y }));
    for (const n of nodes) {
      n.vx = 0;
      n.vy = 0;
    }
    if (staticView || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      tweenRef.current = null;
    } else {
      nodes.forEach((n, i) => ((n.x = from[i]!.x), (n.y = from[i]!.y)));
      tweenRef.current = { nodes, from, to, start: performance.now(), dur: alpha >= 0.9 ? 900 : 650 };
    }
    dirtyRef.current = true;
  }

  /* ---------------------------------------------------- build simulation */
  useEffect(() => {
    const byId = new Map<string, SimNode>();
    const prev = new Map(nodesRef.current.map((n) => [n.id, n]));

    // Exchanges, routers and pools link to every holder. Rendering them by
    // default buries the actual structure under a star of spokes, so they are
    // hidden until the user explicitly asks for them.
    const visible = showInfrastructure ? data.nodes : data.nodes.filter((n) => !n.hiddenByDefault);
    // A refresh of a map already on screen: sizes glide and new holders fade in.
    const refreshing = prev.size > 0 && !reduceMotion;

    const nodes: SimNode[] = visible.map((n, i) => {
      const old = prev.get(n.id);
      const p = old ?? (refreshing ? freeSpot(n.address, radiusFor(n.percent), nodesRef.current) : seed(n.id, i, visible.length));
      const rT = radiusFor(n.percent);
      const sn: SimNode = {
        ...n,
        x: p.x,
        y: p.y,
        r: refreshing ? (old?.r ?? 0) : rT,
        rT,
        vis: refreshing ? (old?.vis ?? 0) : 1,
        visT: 1,
        vx: 0,
        vy: 0,
      };
      byId.set(n.id, sn);
      return sn;
    });
    // bubbles added from live trades survive a refresh while they still hold
    const lv = liveRef.current;
    for (const old of prev.values()) {
      if (byId.has(old.id)) continue;
      if (old.liveAdded && (lv?.get(old.address) ?? 0) > 0) {
        nodes.push(old);
        byId.set(old.id, old);
      } else if (refreshing) ghostsRef.current.push({ ...old, rT: 0, visT: 0 });
    }

    const links: SimLink[] = [];
    for (const e of data.edges) {
      const s = byId.get(e.source);
      const t = byId.get(e.target);
      if (!s || !t) continue;
      links.push({ ...e, source: s, target: t });
    }

    nodesRef.current = nodes;
    linksRef.current = links;

    simRef.current?.stop();

    // Cluster cohesion: members drift toward their group's centroid so
    // coordinated wallets visually clump instead of scattering.
    const clusterCentroids = new Map<string, { x: number; y: number }>();
    const cohesion = (alpha: number) => {
      clusterCentroids.clear();
      const counts = new Map<string, number>();
      const ov = overlayRef.current;
      const keyOf = (n: SimNode) => (ov ? ov.groupOf.get(n.address) : n.clusterId ? `c${n.clusterId}` : undefined);
      for (const n of nodes) {
        const k = keyOf(n);
        if (!k) continue;
        const c = clusterCentroids.get(k) ?? { x: 0, y: 0 };
        c.x += n.x;
        c.y += n.y;
        clusterCentroids.set(k, c);
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
      for (const [id, c] of clusterCentroids) {
        const n = counts.get(id) ?? 1;
        c.x /= n;
        c.y /= n;
      }
      const strength = 0.22 * alpha;
      for (const n of nodes) {
        const k = keyOf(n);
        if (!k) continue;
        const c = clusterCentroids.get(k)!;
        n.vx = (n.vx ?? 0) + (c.x - n.x) * strength;
        n.vy = (n.vy ?? 0) + (c.y - n.y) * strength;
      }
    };

    const sim = forceSimulation<SimNode>(nodes)
      .force('charge', forceManyBody<SimNode>().strength((d) => -38 - d.r * 3.4).distanceMax(900))
      .force(
        'link',
        forceLink<SimNode, SimLink>(links)
          .id((d) => d.id)
          .distance((l) => 46 + l.source.r + l.target.r - 24 * l.weight)
          .strength((l) => 0.08 + 0.42 * l.weight),
      )
      .force('collide', forceCollide<SimNode>().radius((d) => Math.max(d.r, d.rT) + 4.5).iterations(3))
      .force('x', forceX<SimNode>(0).strength(0.028))
      .force('y', forceY<SimNode>(0).strength(0.04))
      .force('cohesion', cohesion)
      .alphaDecay(0.028)
      .velocityDecay(0.4)
      .on('tick', () => (dirtyRef.current = true))
      .stop(); // never free-runs: layouts are computed off-screen (settle) and glided to

    simRef.current = sim;
    // A refresh of the same holders barely nudges the layout; a new token lays out from scratch.
    const known = nodes.filter((n) => prev.has(n.id)).length;
    const fresh = nodes.length ? 1 - known / nodes.length : 1;
    // Live refreshes of a map on screen integrate gently: new holders already
    // sit on free spots, so only a light relax is needed (no reshuffle).
    if (fresh > 0 || prev.size !== nodes.length) settle(prev.size === 0 ? 1 : fresh > 0.6 ? 0.5 : fresh > 0.1 ? 0.15 : 0.08);
    dirtyRef.current = true;
    return () => {
      sim.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, showInfrastructure]);

  // New group assignment: pull members together, once, smoothly. Keyed by
  // content so the poller handing us an equal-but-new Map does nothing.
  const groupSig = useMemo(
    () => (overlay ? [...overlay.groupOf.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map((e) => e.join('=')).join('|') : ''),
    [overlay],
  );
  const lastGroupSig = useRef('');
  useEffect(() => {
    if (lastGroupSig.current === groupSig) return;
    const first = lastGroupSig.current === '';
    lastGroupSig.current = groupSig;
    if (simRef.current && groupSig) settle(first ? 0.45 : 0.12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSig]);

  /* ---------------------------------------------------- live holdings
     Trades since the snapshot, replayed by the page at their real pace.
     Sizes ease to their new value; a wallet that sold everything shrinks
     and fades away; a new buyer big enough for the map fades in on a free
     spot. When bubbles grow into their neighbours, the layout relaxes a
     little (rate-limited, glided) instead of jumping. */
  useEffect(() => {
    if (!live || !live.size || !simRef.current) return;
    const nodes = nodesRef.current;
    const byAddr = new Map(nodes.map((n) => [n.address, n]));
    // A newcomer earns a bubble once it holds as much as the smallest holder
    // in the snapshot (live-shrunk bubbles must not lower the bar).
    const snapHolders = data.nodes.filter((n) => n.origin !== 'magic' && !n.hiddenByDefault);
    const minPct = Math.max(0.01, snapHolders.reduce((m, n) => Math.min(m, n.percent), Infinity));
    const liveCount = nodes.filter((n) => n.liveAdded).length;
    let grew = false;
    let changed = false;
    let added = 0;
    let gone = 0;
    for (const [addr, pct] of live) {
      const n = byAddr.get(addr);
      if (n) {
        if (n.origin === 'magic' || Math.abs(n.percent - pct) < 1e-9) continue;
        changed = true;
        n.percent = Math.max(0, pct);
        if (pct <= 1e-6) {
          // sold out: shrink + fade, then gone
          ghostsRef.current.push({ ...n, rT: 0, visT: 0 });
          gone++;
          nodesRef.current = nodesRef.current.filter((x) => x !== n);
          linksRef.current = linksRef.current.filter((l) => l.source !== n && l.target !== n);
          continue;
        }
        const rT = radiusFor(pct);
        if (rT > n.rT + 1.5) grew = true;
        n.rT = rT;
        if (reduceMotion) n.r = rT;
      } else if (pct >= minPct && added < 12 && liveCount + added < 40) {
        const rT = radiusFor(pct);
        const p = freeSpot(addr, rT, nodesRef.current);
        const sn: SimNode = {
          id: `${data.token.chain}:${addr}`,
          address: addr,
          chain: data.token.chain,
          rank: 0,
          amount: 0,
          percent: pct,
          kind: 'eoa',
          tags: [],
          origin: 'holder',
          hiddenByDefault: false,
          x: p.x,
          y: p.y,
          r: reduceMotion ? rT : 0,
          rT,
          vis: reduceMotion ? 1 : 0,
          visT: 1,
          vx: 0,
          vy: 0,
          liveAdded: true,
        };
        nodesRef.current = [...nodesRef.current, sn];
        byAddr.set(addr, sn);
        added++;
        changed = true;
      }
    }
    if (!changed) return;
    dirtyRef.current = true;
    // counters for tests / debugging (no visual effect)
    const ds = wrapRef.current?.dataset;
    if (ds) {
      ds.liveUpdates = String(Number(ds.liveUpdates ?? 0) + 1);
      ds.liveAdded = String(Number(ds.liveAdded ?? 0) + added);
      ds.liveGone = String(Number(ds.liveGone ?? 0) + gone);
    }
    if (grew || added) {
      const rl = relaxRef.current;
      clearTimeout(rl.timer);
      const wait = Math.max(500, 2500 - (performance.now() - rl.last));
      rl.timer = setTimeout(() => {
        rl.last = performance.now();
        settle(0.06);
      }, wait);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, data]);
  useEffect(() => () => clearTimeout(relaxRef.current.timer), []);

  /* ------------------------------------------------------------ resizing */
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      sizeRef.current = { w: rect.width, h: rect.height };
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (transformRef.current.x === 0 && transformRef.current.y === 0) {
        transformRef.current = { k: 1, x: rect.width / 2, y: rect.height / 2 };
      }
      dirtyRef.current = true;
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  /* ------------------------------------------------------------ fit view */
  const fit = useCallback((animate = true) => {
    const nodes = nodesRef.current;
    const { w, h } = sizeRef.current;
    if (!nodes.length || !w || !h) return;
    const tw = tweenRef.current;
    const pos = (n: SimNode, i: number) => (tw && tw.nodes === nodes ? tw.to[i]! : n);
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    nodes.forEach((n, i) => {
      const p = pos(n, i);
      minX = Math.min(minX, p.x - n.r);
      maxX = Math.max(maxX, p.x + n.r);
      minY = Math.min(minY, p.y - n.r);
      maxY = Math.max(maxY, p.y + n.r);
    });
    const pad = w < 500 ? 28 : 60;
    const k = Math.max(0.12, Math.min(2.6, Math.min((w - pad) / (maxX - minX || 1), (h - pad) / (maxY - minY || 1))));
    const target = { k, x: w / 2 - ((minX + maxX) / 2) * k, y: h / 2 - ((minY + maxY) / 2) * k };
    if (animate && !staticView) camTweenRef.current = { from: { ...transformRef.current }, to: target, start: performance.now(), dur: 600 };
    else transformRef.current = target;
    dirtyRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Zoom around the centre of the view (buttons) or a given screen point. */
  const zoomAt = useCallback((factor: number, sx?: number, sy?: number) => {
    const { w, h } = sizeRef.current;
    const t = transformRef.current;
    const mx = sx ?? w / 2;
    const my = sy ?? h / 2;
    const k = Math.max(0.08, Math.min(7, t.k * factor));
    transformRef.current = { k, x: mx - ((mx - t.x) / t.k) * k, y: my - ((my - t.y) / t.k) * k };
    dirtyRef.current = true;
  }, []);

  // Auto-fit only for a new token or a very different holder set; a refresh
  // must never yank the camera away from where the user is looking.
  useEffect(() => {
    const key = `${data.token?.address ?? ''}|${Math.round(Math.log2(Math.max(1, data.nodes.length)) * 2)}`;
    if (fittedKeyRef.current === key) return;
    const first = !fittedKeyRef.current;
    fittedKeyRef.current = key;
    const id = setTimeout(() => fit(!first), first ? 0 : 200);
    return () => clearTimeout(id);
  }, [data, fit]);

  /* ------------------------------------------------- HTML overlays as label obstacles
     The map key, zoom buttons, status pills etc. sit on top of the canvas;
     labels must not hide underneath them. Re-measured every 400 ms and the
     map redraws when they move, open or close. */
  const overlayBoxesRef = useRef<{ x: number; y: number; w: number; h: number }[]>([]);
  useEffect(() => {
    const measure = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const c = canvas.getBoundingClientRect();
      const boxes: { x: number; y: number; w: number; h: number }[] = [];
      for (const el of document.querySelectorAll<HTMLElement>(LABEL_OBSTACLES)) {
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height || r.right < c.left || r.left > c.right || r.bottom < c.top || r.top > c.bottom) continue;
        boxes.push({ x: r.left - c.left, y: r.top - c.top, w: r.width, h: r.height });
      }
      const sig = boxes.map((b) => `${b.x | 0},${b.y | 0},${b.w | 0},${b.h | 0}`).join(';');
      const prev = overlayBoxesRef.current.map((b) => `${b.x | 0},${b.y | 0},${b.w | 0},${b.h | 0}`).join(';');
      if (sig !== prev) {
        overlayBoxesRef.current = boxes;
        dirtyRef.current = true;
      }
    };
    measure();
    const id = setInterval(measure, 400);
    return () => clearInterval(id);
  }, []);

  /* ---------------------------------------------------------- render loop */
  useEffect(() => {
    let raf = 0;

    dirtyRef.current = true;
    const draw = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) {
        raf = requestAnimationFrame(draw);
        return;
      }
      const now = performance.now();
      const ease = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
      const tw = tweenRef.current;
      if (tw) {
        const k = Math.min(1, (now - tw.start) / tw.dur);
        const e = ease(k);
        tw.nodes.forEach((n, i) => {
          n.x = tw.from[i]!.x + (tw.to[i]!.x - tw.from[i]!.x) * e;
          n.y = tw.from[i]!.y + (tw.to[i]!.y - tw.from[i]!.y) * e;
        });
        if (k >= 1) tweenRef.current = null;
        dirtyRef.current = true;
      }
      const ct = camTweenRef.current;
      if (ct) {
        const k = Math.min(1, (now - ct.start) / ct.dur);
        const e = ease(k);
        transformRef.current = {
          k: ct.from.k + (ct.to.k - ct.from.k) * e,
          x: ct.from.x + (ct.to.x - ct.from.x) * e,
          y: ct.from.y + (ct.to.y - ct.from.y) * e,
        };
        if (k >= 1) camTweenRef.current = null;
        dirtyRef.current = true;
      }
      // ease sizes / opacity (frame-rate independent, ~200 ms time constant)
      const dt = Math.min(64, now - (lastFrameRef.current || now));
      lastFrameRef.current = now;
      const a = 1 - Math.exp(-dt / 200);
      let easing = false;
      for (const n of nodesRef.current) {
        if (Math.abs(n.rT - n.r) > 0.02 || Math.abs(n.visT - n.vis) > 0.005) {
          n.r += (n.rT - n.r) * a;
          n.vis += (n.visT - n.vis) * a;
          easing = true;
        } else if (n.r !== n.rT || n.vis !== n.visT) {
          n.r = n.rT;
          n.vis = n.visT;
          easing = true;
        }
      }
      if (ghostsRef.current.length) {
        for (const g of ghostsRef.current) {
          g.r += (0 - g.r) * a * 0.8;
          g.vis += (0 - g.vis) * a;
        }
        ghostsRef.current = ghostsRef.current.filter((g) => g.vis > 0.02);
        easing = true;
      }
      if (easing) dirtyRef.current = true;
      const pulsing = pulsesRef.current.some((p) => now - p.at < 2600);
      if (!dirtyRef.current && !pulsing) {
        raf = requestAnimationFrame(draw);
        return;
      }
      dirtyRef.current = false;
      const { w, h } = sizeRef.current;
      const t = transformRef.current;
      const nodes = nodesRef.current;
      const links = linksRef.current;

      ctx.clearRect(0, 0, w, h);
      ctx.save();
      ctx.translate(t.x, t.y);
      ctx.scale(t.k, t.k);

      const focusId = hoverNode?.id ?? selectedId;
      const neighbours = new Set<string>();
      if (focusId && focusMode) {
        neighbours.add(focusId);
        for (const l of links) {
          if (l.source.id === focusId) neighbours.add(l.target.id);
          else if (l.target.id === focusId) neighbours.add(l.source.id);
        }
      }
      const dimming = focusMode && Boolean(focusId);

      /* ---- edges ---- */
      ctx.lineCap = 'round';
      for (const l of links) {
        const active = !dimming || (neighbours.has(l.source.id) && neighbours.has(l.target.id));
        const highlighted = highlightIds
          ? highlightIds.has(l.source.id) && highlightIds.has(l.target.id)
          : false;
        // Links inside a cluster inherit the cluster colour — it is what makes
        // a coordinated group read as one object instead of a tangle.
        const ov = overlayRef.current;
        const sharedCluster = ov
          ? ov.groupOf.get(l.source.address) && ov.groupOf.get(l.source.address) === ov.groupOf.get(l.target.address)
            ? ov.colors.get(l.source.address)
            : undefined
          : !groupsOnly && l.source.clusterId && l.source.clusterId === l.target.clusterId
            ? clusterColors.get(l.source.clusterId)
            : undefined;
        ctx.globalAlpha = active ? (highlighted ? 1 : sharedCluster ? 0.78 : 0.55) : 0.06;
        ctx.strokeStyle = sharedCluster ?? EDGE_COLOR[l.kind] ?? EDGE_COLOR.transfer!;
        ctx.lineWidth = Math.max(0.5, (0.8 + l.weight * 2.8) / Math.sqrt(t.k));
        if (l.kind === 'time') ctx.setLineDash([5 / t.k, 4 / t.k]);
        else ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(l.source.x, l.source.y);
        ctx.lineTo(l.target.x, l.target.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      /* ---- flagged groups: a ring around each linked group, in its risk
         colour (red = flagged, amber = suspicious, blue = low risk). ---- */
      const hulls: { id: string; x: number; y: number; r: number; color: string; level: string; tag: string; percent: number }[] = [];
      const ovG = overlayRef.current?.groups;
      if (ovG?.length) {
        const byAddr = new Map(nodes.map((n) => [n.address, n]));
        for (const g of ovG) {
          const ms = g.members.map((a) => byAddr.get(a)).filter((n): n is SimNode => Boolean(n));
          if (ms.length < 2) continue;
          let sx = 0;
          let sy = 0;
          let sw = 0;
          for (const m of ms) {
            const wgt = m.r * m.r;
            sx += m.x * wgt;
            sy += m.y * wgt;
            sw += wgt;
          }
          const cx = sx / sw;
          const cy = sy / sw;
          let r = 0;
          for (const m of ms) r = Math.max(r, Math.hypot(m.x - cx, m.y - cy) + m.r);
          r += 10 / Math.sqrt(t.k);
          hulls.push({ id: g.id, x: cx, y: cy, r, color: g.color, level: g.level, tag: g.tag, percent: g.percent });
          const focusG = !dimming || ms.some((m) => neighbours.has(m.id));
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.globalAlpha = focusG ? (g.level === 'high' ? 0.08 : 0.04) : 0.02;
          ctx.fillStyle = g.color;
          ctx.fill();
          ctx.globalAlpha = focusG ? (g.level === 'high' ? 0.75 : 0.5) : 0.15;
          ctx.strokeStyle = g.color;
          ctx.lineWidth = (g.level === 'high' ? 1.6 : 1.1) / t.k;
          ctx.setLineDash([5 / t.k, 5 / t.k]);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.globalAlpha = 1;
      }

      /* ---- nodes (magic nodes drawn last so hubs are never buried) ---- */
      // Screen-space boxes already taken by badges; labels route around them.
      const badgeBoxes: { x: number; y: number; w: number; h: number }[] = [];
      const drawOrder = [...nodes].sort(
        (a, b) => (a.origin === 'magic' ? 1 : 0) - (b.origin === 'magic' ? 1 : 0) || b.r - a.r,
      );
      for (const g of ghostsRef.current) {
        ctx.globalAlpha = 0.85 * g.vis;
        ctx.beginPath();
        ctx.arc(g.x, g.y, Math.max(0.1, g.r), 0, Math.PI * 2);
        ctx.fillStyle = nodeColor(g, clusterColors, overlayRef.current, groupsOnly);
        ctx.fill();
        ctx.strokeStyle = '#f6465d';
        ctx.lineWidth = 1.4 / t.k;
        ctx.stroke();
      }
      for (const n of drawOrder) {
        const active = !dimming || neighbours.has(n.id);
        const fade = n.vis;
        const isHighlighted = highlightIds?.has(n.id) ?? false;
        const ov = overlayRef.current;
        const color = nodeColor(n, clusterColors, ov, groupsOnly);
        const grouped = ov ? ov.groupOf.has(n.address) : !groupsOnly && Boolean(n.clusterId);
        const magic = n.origin === 'magic';

        ctx.globalAlpha = active ? 1 : 0.14;

        // glow for grouped / highlighted bubbles
        if ((grouped || isHighlighted) && active && t.k > 0.3) {
          ctx.shadowColor = color;
          ctx.shadowBlur = isHighlighted ? 22 : 11;
        } else {
          ctx.shadowBlur = 0;
        }

        // Funding wallets (not holders) are hollow dashed rings.
        ctx.beginPath();
        ctx.arc(n.x, n.y, Math.max(0.1, n.r), 0, Math.PI * 2);
        ctx.fillStyle = magic ? BRAND.canvasBg : color;
        ctx.globalAlpha = (active ? (magic ? 0.9 : n.hiddenByDefault ? 0.4 : grouped ? 0.9 : 0.66) : 0.12) * fade;
        ctx.fill();
        ctx.shadowBlur = 0;

        ctx.globalAlpha = (active ? 1 : 0.16) * fade;
        ctx.lineWidth = (magic ? 1.8 : 1.4) / Math.sqrt(t.k);
        ctx.strokeStyle = magic ? (grouped ? color : 'rgba(205,210,220,0.75)') : 'rgba(5,6,8,0.9)';
        if (magic) ctx.setLineDash([3.5 / t.k, 2.5 / t.k]);
        ctx.stroke();
        ctx.setLineDash([]);

        // a second, coloured ring keeps touching bubbles visually separate
        if (!magic) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, Math.max(0.5, n.r - 0.6 / t.k), 0, Math.PI * 2);
          ctx.strokeStyle = color;
          ctx.lineWidth = 1 / Math.sqrt(t.k);
          ctx.stroke();
        }

        if (n.id === selectedId) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 5 / t.k, 0, Math.PI * 2);
          ctx.strokeStyle = BRAND.accent;
          ctx.lineWidth = 2.5 / t.k;
          ctx.stroke();
        }

        // live trade pulse: an expanding ring for ~2.5 s
        const pulse = pulsesRef.current.find((p) => p.address === n.address);
        if (pulse) {
          const age = (performance.now() - pulse.at) / 2500;
          if (age >= 0 && age < 1) {
            ctx.globalAlpha = 1 - age;
            ctx.beginPath();
            ctx.arc(n.x, n.y, n.r + (4 + age * 18) / t.k, 0, Math.PI * 2);
            ctx.strokeStyle = pulse.side === 'buy' ? '#1fc58e' : '#f6465d';
            ctx.lineWidth = 2.5 / t.k;
            ctx.stroke();
            ctx.globalAlpha = active ? 1 : 0.16;
          }
        }

        // role badge (DEV, G1, BDL...) from the terminal overlay
        const badge = ov?.badges.get(n.address);
        if (badge && active && n.r * t.k > 7) {
          const fs = 9 / t.k;
          ctx.font = `700 ${fs}px Geist, sans-serif`;
          const w = ctx.measureText(badge.text).width + 8 / t.k;
          const h = 13 / t.k;
          const bx = n.x + n.r * 0.55;
          const by = n.y - n.r * 0.95 - h / 2;
          ctx.globalAlpha = 1;
          ctx.fillStyle = badge.color;
          ctx.beginPath();
          ctx.roundRect(bx - w / 2, by - h / 2, w, h, 4 / t.k);
          ctx.fill();
          ctx.fillStyle = '#05070d';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(badge.text, bx, by + 0.5 / t.k);
          badgeBoxes.push({ x: t.x + (bx - w / 2) * t.k, y: t.y + (by - h / 2) * t.k, w: w * t.k, h: h * t.k });
        }

        // risk marker for insider-ish wallets
        if (!ov && !groupsOnly && !staticView && active && (n.tags.includes('bundler') || n.tags.includes('sniper')) && n.r * t.k > 9) {
          ctx.beginPath();
          ctx.arc(n.x + n.r * 0.68, n.y - n.r * 0.68, Math.max(2, 3 / t.k), 0, Math.PI * 2);
          ctx.fillStyle = n.tags.includes('bundler') ? '#ff4d6d' : '#ffc53d';
          ctx.fill();
        }

      }

      ctx.globalAlpha = 1;
      ctx.restore();

      /* ---- labels -----------------------------------------------------
         Placed in screen space with collision avoidance: biggest holders
         first, inside the bubble when it fits, otherwise on a dark pill
         beside it, and dropped when there's no free spot. Hover / selection
         always get theirs. Funding wallets hold nothing, so no % label. */
      if (showLabels) {
        const screen = (n: SimNode) => ({ x: t.x + n.x * t.k, y: t.y + n.y * t.k, r: n.r * t.k });
        const visible = nodes.filter((n) => {
          const p = screen(n);
          return p.x + p.r > 0 && p.y + p.r > 0 && p.x - p.r < w && p.y - p.r < h;
        });
        const circles = visible.map((n) => ({ id: n.id, ...screen(n) }));
        const reqs: LabelRequest[] = [];
        const meta = new Map<string, { lines: string[]; inside: boolean; fs: number }>();
        for (const n of visible) {
          if (n.origin === 'magic' || n.vis < 0.6) continue;
          const forced = n.id === hoverNode?.id || n.id === selectedId;
          if (dimming && !neighbours.has(n.id) && !forced) continue;
          // Dust (<0.01%) only adds noise; it's still on hover.
          if (!(n.percent >= 0.01) && !forced) continue;
          const p = screen(n);
          if (p.r < 2.5 && !forced) continue;
          const pct = pctLabel(n.percent);
          // Inside a big bubble: name (if any) over the share.
          const insideFs = Math.min(14, Math.max(10, p.r * 0.32));
          ctx.font = `650 ${insideFs}px Geist, sans-serif`;
          const named = n.label ? (n.label.length > 16 ? `${n.label.slice(0, 15)}…` : n.label) : undefined;
          const lines = named && p.r > 34 ? [named, pct] : [pct];
          const inW = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 6;
          const inH = lines.length * (insideFs + 2) + 2;
          if (fitsInside(inW, inH, p.r, 2)) {
            meta.set(n.id, { lines, inside: true, fs: insideFs });
            reqs.push({ id: n.id, x: p.x, y: p.y, r: p.r, w: inW, h: inH, priority: n.percent + (forced ? 1e6 : 0), force: forced });
            continue;
          }
          ctx.font = `600 11px Geist, sans-serif`;
          const outW = ctx.measureText(pct).width + 10;
          meta.set(n.id, { lines: [pct], inside: false, fs: 11 });
          reqs.push({ id: n.id, x: p.x, y: p.y, r: p.r, w: outW, h: 16, priority: n.percent + (forced ? 1e6 : 0), force: forced });
        }
        // Risk tags for groups go first and win their spot; % labels route around them.
        const lvRank: Record<string, number> = { high: 3e6, medium: 2e6, low: 1e6 };
        ctx.font = `600 10.5px "Geist Mono", ui-monospace, monospace`;
        const tagReqs: LabelRequest[] = hulls.map((g) => ({
          id: g.id,
          x: t.x + g.x * t.k,
          y: t.y + g.y * t.k,
          r: g.r * t.k,
          w: ctx.measureText(g.tag).width + 26,
          h: 20,
          priority: lvRank[g.level]! + g.percent,
        }));
        const tags = layoutLabels(tagReqs, {
          width: w,
          height: h,
          obstacles: [...overlayBoxesRef.current],
          slots: ['above', 'below', 'right', 'left'],
          gap: 4,
          pad: 4,
        });
        const hullById = new Map(hulls.map((g) => [g.id, g]));
        for (const tg of tags) {
          const g = hullById.get(tg.id)!;
          const b = tg.box;
          ctx.globalAlpha = 1;
          ctx.fillStyle = 'rgba(6,7,9,0.9)';
          ctx.beginPath();
          ctx.roundRect(b.x, b.y, b.w, b.h, 5);
          ctx.fill();
          ctx.strokeStyle = g.color;
          ctx.globalAlpha = 0.7;
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.globalAlpha = 1;
          // diamond marker, like the "trap detected" mark on trapmap.fun
          const dx = b.x + 10;
          const dy = b.y + b.h / 2;
          ctx.fillStyle = g.color;
          ctx.beginPath();
          ctx.moveTo(dx, dy - 3.5);
          ctx.lineTo(dx + 3.5, dy);
          ctx.lineTo(dx, dy + 3.5);
          ctx.lineTo(dx - 3.5, dy);
          ctx.closePath();
          ctx.fill();
          ctx.font = `600 10.5px "Geist Mono", ui-monospace, monospace`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(g.tag, b.x + 18, dy + 0.5);
        }

        const max = Math.max(12, Math.min(60, Math.round((w * h) / 9000)));
        const placed = layoutLabels(reqs, {
          width: w,
          height: h,
          circles,
          obstacles: [...badgeBoxes, ...overlayBoxesRef.current, ...tags.map((tg) => tg.box)],
          max,
          pad: 3,
          gap: 3,
        });
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (const pl of placed) {
          const m = meta.get(pl.id)!;
          if (pl.slot === 'inside') {
            ctx.font = `650 ${m.fs}px Geist, sans-serif`;
            ctx.fillStyle = 'rgba(255,255,255,0.96)';
            ctx.shadowColor = 'rgba(0,0,0,0.55)';
            ctx.shadowBlur = 3;
            const lh = m.fs + 2;
            m.lines.forEach((line, i) => {
              ctx.globalAlpha = i === 0 || m.lines.length === 1 ? 1 : 0.72;
              ctx.fillText(line, pl.cx, pl.cy + (i - (m.lines.length - 1) / 2) * lh);
            });
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;
          } else {
            const b = pl.box;
            ctx.fillStyle = 'rgba(6,7,9,0.84)';
            ctx.beginPath();
            ctx.roundRect(b.x, b.y, b.w, b.h, 5);
            ctx.fill();
            ctx.strokeStyle = 'rgba(255,255,255,0.1)';
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.font = `600 11px Geist, sans-serif`;
            ctx.fillStyle = 'rgba(238,241,246,0.95)';
            ctx.fillText(m.lines[0]!, pl.cx, pl.cy + 0.5);
          }
        }
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [clusterColors, focusMode, groupsOnly, highlightIds, hoverNode, overlay, selectedId, showLabels]);

  /* ------------------------------------------------------------ picking */
  const toWorld = useCallback((clientX: number, clientY: number) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const t = transformRef.current;
    return {
      x: (clientX - rect.left - t.x) / t.k,
      y: (clientY - rect.top - t.y) / t.k,
    };
  }, []);

  const pick = useCallback(
    (clientX: number, clientY: number): SimNode | undefined => {
      const { x, y } = toWorld(clientX, clientY);
      let best: SimNode | undefined;
      let bestD = Infinity;
      for (const n of nodesRef.current) {
        if (n.vis < 0.4) continue;
        const dx = n.x - x;
        const dy = n.y - y;
        const d = Math.hypot(dx, dy);
        if (d <= n.r + 3 && d < bestD) {
          best = n;
          bestD = d;
        }
      }
      return best;
    },
    [toWorld],
  );

  /* -------------------------------------------------------- interactions */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || staticView) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0016), e.clientX - rect.left, e.clientY - rect.top);
    };

    // Two fingers: pinch-zoom around their midpoint (and pan with it).
    const pinchState = () => {
      const pts = [...touchesRef.current.values()];
      if (pts.length < 2) return undefined;
      const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
      const rect = canvas.getBoundingClientRect();
      return { d: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2 - rect.left, cy: (a.y + b.y) / 2 - rect.top };
    };

    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'touch') {
        touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (touchesRef.current.size === 2) {
          // second finger: cancel any node drag / pan and start a pinch
          const d = dragRef.current;
          if (d.node) {
            d.node.fx = null;
            d.node.fy = null;
            simRef.current?.alphaTarget(0);
          }
          dragRef.current = { panning: false, lastX: 0, lastY: 0, moved: true };
          const p = pinchState();
          const t = transformRef.current;
          if (p) pinchRef.current = { d: p.d, k: t.k, wx: (p.cx - t.x) / t.k, wy: (p.cy - t.y) / t.k };
          return;
        }
      }
      canvas.setPointerCapture(e.pointerId);
      const node = pick(e.clientX, e.clientY);
      dragRef.current = {
        node,
        panning: !node,
        lastX: e.clientX,
        lastY: e.clientY,
        moved: false,
      };
      setDragging(true);
      camTweenRef.current = null;
      if (node) {
        const tw = tweenRef.current;
        if (tw) {
          tw.nodes.forEach((n, i) => ((n.x = tw.to[i]!.x), (n.y = tw.to[i]!.y)));
          tweenRef.current = null;
        }
        simRef.current?.alphaTarget(0.12).restart();
        node.fx = node.x;
        node.fy = node.y;
      }
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch' && touchesRef.current.has(e.pointerId)) {
        touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const start = pinchRef.current;
        const p = start && pinchState();
        if (start && p) {
          // zoom by the finger spread and keep the pinched point under the fingers
          const k = Math.max(0.08, Math.min(7, start.k * (p.d / Math.max(1, start.d))));
          transformRef.current = { k, x: p.cx - start.wx * k, y: p.cy - start.wy * k };
          dirtyRef.current = true;
          return;
        }
      }
      const d = dragRef.current;
      if (d.node) {
        const { x, y } = toWorld(e.clientX, e.clientY);
        d.node.fx = x;
        d.node.fy = y;
        d.moved = true;
        return;
      }
      if (d.panning) {
        const t = transformRef.current;
        const dx = e.clientX - d.lastX;
        const dy = e.clientY - d.lastY;
        transformRef.current = { ...t, x: t.x + dx, y: t.y + dy };
        d.lastX = e.clientX;
        d.lastY = e.clientY;
        // a few px of finger jitter is still a tap
        if (Math.abs(dx) + Math.abs(dy) > 2) d.moved = true;
        dirtyRef.current = true;
        return;
      }
      if (e.pointerType === 'touch') return;
      const node = pick(e.clientX, e.clientY);
      if (node?.id !== hoverNode?.id) {
        setHoverNode(node);
        onHover?.(node);
      }
      if (node && node.id !== hoverNode?.id) {
        // Anchor to the bubble's right edge (not the cursor), so the card stays still while you read it.
        const t = transformRef.current;
        setTooltipPos({ x: t.x + (node.x + node.r) * t.k + 12, y: t.y + node.y * t.k - 24 });
      }
    };

    const onUp = (e: PointerEvent) => {
      if (e.pointerType === 'touch') {
        const wasPinch = touchesRef.current.size >= 2 || pinchRef.current !== undefined;
        touchesRef.current.delete(e.pointerId);
        if (touchesRef.current.size < 2) pinchRef.current = undefined;
        if (wasPinch) {
          // the last finger lifting after a pinch is not a tap
          setDragging(false);
          dragRef.current = { panning: false, lastX: 0, lastY: 0, moved: true };
          return;
        }
      }
      const d = dragRef.current;
      setDragging(false);
      if (d.node) {
        simRef.current?.alphaTarget(0);
        d.node.fx = null;
        d.node.fy = null;
        if (!d.moved) onSelect(d.node);
      } else if (!d.moved) {
        onSelect(undefined);
      }
      dragRef.current = { panning: false, lastX: 0, lastY: 0, moved: false };
      canvas.releasePointerCapture?.(e.pointerId);
    };

    const onLeave = () => {
      setHoverNode(undefined);
      onHover?.(undefined);
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('pointercancel', onUp);
    return () => {
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onLeave);
    };
  }, [hoverNode, onHover, onSelect, pick, staticView, toWorld, zoomAt]);

  /* ---------------------------------------------------------- public api */
  useEffect(() => {
    if (!onReady) return;
    onReady({
      fit,
      zoomBy: (f: number) => zoomAt(f),
      reheat: () => settle(0.7),
      exportPng: (filename: string, title?: string) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Composite onto an opaque background so the PNG is shareable as-is.
        const out = document.createElement('canvas');
        out.width = canvas.width;
        out.height = canvas.height;
        const ctx = out.getContext('2d')!;
        const s = canvas.width / Math.max(1, sizeRef.current.w);
        ctx.fillStyle = BRAND.canvasBg;
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.drawImage(canvas, 0, 0);
        ctx.font = `700 ${18 * s}px Geist, sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.fillText(title ?? BRAND.name, 20 * s, out.height - 22 * s);
        ctx.font = `500 ${12 * s}px Geist, sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fillText(new Date().toUTCString().slice(5, 22) + ' UTC', 20 * s, out.height - 44 * s);
        const link = document.createElement('a');
        link.download = filename;
        link.href = out.toDataURL('image/png');
        link.click();
      },
    });
  }, [fit, onReady, zoomAt]);

  /* ------------------------------------------------------------- render */
  return (
    <div className="canvas-wrap" ref={wrapRef}>
      <canvas ref={canvasRef} className={dragging ? 'dragging' : ''} />

      {hoverNode && (
        <div
          className="tooltip tm-tip"
          style={{
            left: Math.max(8, Math.min(tooltipPos.x, sizeRef.current.w - 262)),
            top: Math.max(8, Math.min(tooltipPos.y, sizeRef.current.h - 190)),
          }}
        >
          <div className="t-main">
            <span>
              {hoverNode.origin === 'magic'
                ? 'Funding wallet'
                : (hoverNode.label ?? (hoverNode.rank > 0 ? `Holder #${hoverNode.rank}` : 'Wallet'))}
            </span>
            {hoverNode.origin !== 'magic' && <span className="mono t-pct">{formatPercent(hoverNode.percent)}</span>}
          </div>
          <div className="t-addr mono">{shortAddress(hoverNode.address, 6, 6)}</div>
          {hoverNode.origin === 'magic' ? (
            <div className="t-line">Sent money to several holders. Holds none of this token.</div>
          ) : (
            hoverNode.valueUsd !== undefined && <div className="t-line mono">{formatUsd(hoverNode.valueUsd)} worth</div>
          )}
          {!overlay && !groupsOnly && hoverNode.clusterId && (
            <div className="t-line" style={{ color: clusterColors.get(hoverNode.clusterId) }}>
              Linked group #{hoverNode.clusterId}
            </div>
          )}
          {overlay?.describe?.(hoverNode.address)}
          {!staticView && <div className="t-hint">Click for wallet details</div>}
        </div>
      )}

      {/* Every holder is a pool / exchange / contract (e.g. a Pump.fun token
          nobody has bought yet): say so instead of showing a blank canvas. */}
      {!showInfrastructure && data.nodes.length > 0 && data.nodes.every((n) => n.hiddenByDefault) && (
        <InfraOnlyNotice data={data} onShow={onShowInfrastructure} />
      )}

      {data.nodes.length === 0 && (
        <div className="canvas-empty">
          <div>
            <div style={{ fontSize: 30, marginBottom: 10 }}>○</div>
            No holders found for this snapshot.
            <br />
            Try widening the holder limit or moving the time cursor forward.
          </div>
        </div>
      )}
    </div>
  );
}

function InfraOnlyNotice({ data, onShow }: { data: MapData; onShow?: () => void }) {
  const top = [...data.nodes].sort((a, b) => b.percent - a.percent)[0]!;
  const preTrade = top.kind === 'dex' || top.kind === 'lp';
  return (
    <div className="canvas-empty">
      <div style={{ maxWidth: 440 }}>
        <div style={{ fontSize: 30, marginBottom: 10 }}>◌</div>
        <div style={{ fontWeight: 650, marginBottom: 6 }}>
          {preTrade ? 'No one holds this token yet' : 'No individual holders to map'}
        </div>
        <div className="muted" style={{ lineHeight: 1.55 }}>
          {top.percent >= 99.95 ? 'All' : `${top.percent.toFixed(1)}% of`} the supply sits in{' '}
          <strong>{top.label ?? `a ${top.kind} address`}</strong>
          {data.nodes.length > 1 ? ` and ${data.nodes.length - 1} other pool / contract address(es)` : ''}.{' '}
          {preTrade
            ? 'Bubbles appear as soon as wallets buy — refresh after the first trades.'
            : 'Pools, exchanges and contracts are hidden by default because they are not people.'}
        </div>
        {onShow && (
          <button className="btn" style={{ marginTop: 16 }} onClick={onShow}>
            Show pools &amp; contracts
          </button>
        )}
      </div>
    </div>
  );
}
